/**
 * Google Consent Mode v2 — the "default" half.
 *
 * Must run BEFORE any Google tag (GTM/GA) loads, otherwise tags fire while the
 * consent state is still unset and behave as if consent had been given. A
 * regular Nuxt plugin runs in the client bundle, i.e. potentially after a GTM
 * snippet in <head> — so the call is injected as an inline head script during
 * SSR, with `tagPriority: 'critical'` to keep it ahead of everything else.
 *
 * The "update" half lives in plugins/cookieconsent.client.ts, which calls
 * gtag('consent', 'update', …) whenever the visitor changes their choice.
 *
 * No GTM container ID is needed here, and none is hardcoded: the container is
 * expected to be configured from the backend later. This plugin only
 * establishes the consent state the container will read once it does load.
 *
 * How the container is expected to arrive: the administration stores raw
 * markup under `/public/settings/codes` (`codes_head`, `codes_body_start`,
 * `codes_body_end`) — see docs/api/settings.md. Nothing in this project reads
 * that endpoint yet; the `server/plugins/extend-html.ts` Nitro plugin the docs
 * refer to does not exist, so a GTM snippet configured in the administration
 * currently never reaches the page.
 *
 * WHEN SOMEBODY WIRES IT UP: inject `codes_head` with `html.head.push()` in a
 * `render:html` hook — push, never prepend. `useHead` output is emitted first,
 * so appending keeps the consent 'default' call above the container. A
 * container that loads before it fires its tags with consent unset, which is
 * exactly what Consent Mode is meant to prevent. Verify the resulting order in
 * the rendered HTML (and in Google Tag Assistant) rather than assuming it.
 */

/** Shape of the cookie written by vanilla-cookieconsent. */
type ConsentCookie = { categories?: string[] }

export default defineNuxtPlugin(() => {
  // Read server-side so a returning visitor's stored consent is already in the
  // HTML — without it every page load would start at `denied` and only flip
  // after hydration, dropping measurement for the first few hundred ms.
  const stored = useCookie<ConsentCookie | null>('cc_cookie').value
  const categories = Array.isArray(stored?.categories) ? stored.categories : []
  const state = (category: string) => (categories.includes(category) ? 'granted' : 'denied')

  const analytics = state('analytics')
  const marketing = state('marketing')

  useHead({
    script: [
      {
        key: 'gtag-consent-default',
        tagPriority: 'critical',
        tagPosition: 'head',
        innerHTML: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('consent','default',{'ad_storage':'${marketing}','ad_user_data':'${marketing}','ad_personalization':'${marketing}','analytics_storage':'${analytics}','functionality_storage':'granted','security_storage':'granted','wait_for_update':500});`,
      },
    ],
  })
})
